// src/modules/Firm/pages/TeamMembersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Popconfirm,
  Space,
  message,
  Select,
  Alert,
  Tag,
  Card,
  Avatar,
  Divider,
  Row,
  Col,
  InputNumber
} from "antd";
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined
} from "@ant-design/icons";
import { fetchTeams, createTeam, updateTeam, deleteTeam } from "../../../api/teamApi";
import { withBase } from "../../../config/env";

const { Option } = Select;

const TeamMembersPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    designation: '',
    status: '',
    mobile: ''
  });

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const isFirm = user?.role === "firm";
  const firmIdFromUser = user?.id || null;

  const buildImageUrl = (p) => {
    if (!p) return "";
    try {
      return withBase(String(p)) || String(p);
    } catch {
      return String(p);
    }
  };

  const load = async () => {
    if (!firmIdFromUser) return;
    setLoading(true);
    try {
      const data = await fetchTeams({ firm_id: firmIdFromUser });
      setRows(data);
      setFilteredRows(data);
    } catch (e) {
      message.error(e?.message || "Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isFirm) {
      message.warning("This page is intended for firm users.");
    }
    if (!firmIdFromUser) {
      message.error("Missing firm_id in your account. Please contact support.");
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmIdFromUser]);

  // Apply filters whenever filters or rows change
  useEffect(() => {
    const filtered = rows.filter(row => {
      return (
        (filters.name === '' || row.name?.toLowerCase().includes(filters.name.toLowerCase())) &&
        (filters.email === '' || row.email?.toLowerCase().includes(filters.email.toLowerCase())) &&
        (filters.designation === '' || row.designation?.toLowerCase().includes(filters.designation.toLowerCase())) &&
        (filters.status === '' || row.status === filters.status) &&
        (filters.mobile === '' || row.mobile?.includes(filters.mobile))
      );
    });
    setFilteredRows(filtered);
  }, [filters, rows]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setImagePreview(null);
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      name: record.name,
      email: record.email,
      mobile: record.mobile,
      designation: record.designation,
      status: record.status || "Active",
      image: record.image ? [{
        uid: '-1',
        name: 'image.png',
        status: 'done',
        url: buildImageUrl(record.image),
      }] : []
    });
    setImagePreview(record.image ? buildImageUrl(record.image) : null);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteTeam(id);
      message.success("Team member deleted");
      await load();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "Delete failed");
    }
  };

  const onFinish = async (values) => {
    if (!firmIdFromUser) {
      message.error("Cannot submit: firm_id missing in your account.");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = { ...values, firm_id: firmIdFromUser };

      // Handle image upload
      if (Array.isArray(values.image) && values.image.length) {
        payload.image = values.image[0].originFileObj;
      } else if (values.image?.file?.originFileObj) {
        payload.image = values.image.file.originFileObj;
      } else {
        delete payload.image;
      }

      if (editing) {
        await updateTeam(editing.user_id, payload);
        message.success("Team member updated");
      } else {
        await createTeam(payload);
        message.success("Team member created");
      }

      setOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errors ||
        e?.message ||
        "Save failed";
      if (typeof msg === "object") {
        const first = Object.values(msg)[0];
        message.error(String(first));
      } else {
        message.error(String(msg));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleImageChange = (info) => {
    if (info.file.status === 'removed') {
      setImagePreview(null);
      return;
    }

    const file = info.file.originFileObj;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      name: '',
      email: '',
      designation: '',
      status: '',
      mobile: ''
    });
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      width: 90,
      render: (val) => (
        <Avatar
          src={val ? buildImageUrl(val) : null}
          size={48}
          icon={!val && <UserOutlined />}
          style={{ backgroundColor: '#f0f0f0' }}
        />
      ),
    },
    { 
      title: "Name", 
      dataIndex: "name",
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={filters.name}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: <SearchOutlined />
    },
    { 
      title: "Email", 
      dataIndex: "email",
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search email"
            value={filters.email}
            onChange={(e) => handleFilterChange('email', e.target.value)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: <SearchOutlined />
    },
    { 
      title: "Designation", 
      dataIndex: "designation",
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search designation"
            value={filters.designation}
            onChange={(e) => handleFilterChange('designation', e.target.value)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: <SearchOutlined />
    },
    { 
      title: "Phone Number", 
      dataIndex: "mobile", 
      width: 120, 
      render: (v) => v || "-",
      filterDropdown: () => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search phone"
            value={filters.mobile}
            onChange={(e) => handleFilterChange('mobile', e.target.value)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
        </div>
      ),
      filterIcon: <SearchOutlined />
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status) => status ? (
        <Tag
          color={status === "Active" ? "green" : "red"}
          icon={status === "Active" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {status}
        </Tag>
      ) : "-",
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' },
      ],
      onFilter: (value, record) => record.status === value,
      filterMultiple: false,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            style={{ color: '#1890ff' }}
          />
          <Popconfirm
            title="Are you sure to delete this member?"
            onConfirm={() => handleDelete(record.user_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrap">
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Team Members</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={!firmIdFromUser}
          size="large"
        >
          Add Member
        </Button>
      </div>

      {!firmIdFromUser && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="firm_id is missing in your user profile. Please contact support."
        />
      )}

      <Card
        title={
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  placeholder="Search all fields"
                  prefix={<SearchOutlined />}
                  style={{ width: 250 }}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase();
                    setFilteredRows(rows.filter(row => 
                      row.name?.toLowerCase().includes(value) ||
                      row.email?.toLowerCase().includes(value) ||
                      row.designation?.toLowerCase().includes(value) ||
                      row.mobile?.includes(value) ||
                      row.status?.toLowerCase().includes(value)
                    ));
                  }}
                />
                <Button 
                  icon={<FilterOutlined />} 
                  onClick={() => message.info('Use column filters for specific field searches')}
                >
                  Filters
                </Button>
              </div>
            </Col>
            <Col>
              <Button 
                type="link" 
                onClick={resetFilters}
                disabled={Object.values(filters).every(val => !val)}
              >
                Reset Filters
              </Button>
            </Col>
          </Row>
        }
        bordered={false}
        style={{ marginTop: 16 }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredRows}
          columns={columns}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} members`
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        open={open}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {editing ? (
              <>
                <EditOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <span>Edit Team Member</span>
              </>
            ) : (
              <>
                <PlusOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                <span>Add Team Member</span>
              </>
            )}
          </div>
        }
        onCancel={() => {
          setOpen(false);
          setImagePreview(null);
        }}
        onOk={() => form.submit()}
        okText={editing ? "Update" : "Create"}
        confirmLoading={submitLoading}
        destroyOnClose
        width={700}
        bodyStyle={{ padding: '24px 16px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: "Active",
          }}
        >
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ width: '30%' }}>
              <Form.Item
                label="Profile Image"
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList || [])}
              >
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  listType="picture-card"
                  onChange={handleImageChange}
                  showUploadList={false}
                >
                  {imagePreview ? (
                    <Avatar
                      src={imagePreview}
                      size={120}
                      style={{ borderRadius: 8 }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <UploadOutlined style={{ fontSize: 24 }} />
                      <div style={{ marginTop: 8 }}>Upload Photo</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </div>
            
            <div style={{ flex: 1 }}>
              <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <Input placeholder="e.g. John Smith" size="large" />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Enter a valid email" },
                ]}
              >
                <Input placeholder="e.g. john@firm.com" size="large" />
              </Form.Item>

              <Form.Item
                label="Phone Number"
                name="mobile"
                rules={[
                  { required: true, message: "Phone Number is required" },
                  { pattern: /^[0-9]+$/, message: "Please enter numbers only" },
                ]}
              >
                <Input placeholder="e.g. 03xxxxxxxxx" size="large" />
              </Form.Item>
            </div>
          </div>

          <Divider />

          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ width: '50%' }}>
              {!editing && (
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: "Password is required" },
                    { min: 6, message: "Password must be at least 6 characters" }
                  ]}
                >
                  <Input.Password
                    placeholder="Set initial password"
                    size="large"
                  />
                </Form.Item>
              )}

              {editing && (
                <Form.Item
                  label="New Password"
                  name="password"
                  help="Leave blank to keep current password"
                >
                  <Input.Password
                    placeholder="Enter new password"
                    size="large"
                  />
                </Form.Item>
              )}
            </div>

            <div style={{ width: '50%' }}>
              <Form.Item label="User Status" name="status">
                <Select
                  allowClear
                  placeholder="Select status"
                  size="large"
                >
                  <Option value="Active">
                    <Tag color="green">Active</Tag>
                  </Option>
                  <Option value="Inactive">
                    <Tag color="red">Inactive</Tag>
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item label="Designation" name="designation">
                <Input
                  placeholder="e.g. Senior Associate"
                  size="large"
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamMembersPage;